import useYWR, { YWRKey } from "@/features/ui/hooks/use-ywr";
import useFetcher from "@/utils/use-fetcher";

export interface SkuDailyMarketPrice {
  date: string;
  price: number;
}

// 오더룰(컨디션) 적용 상태 — 서버 LineSheetSetOrderRuleStatus 와 같다.
// PENDING 은 아직 적용 전이라 공급가가 컨디션 반영 전 값이다(화면은 "오더룰 확인 필요"로 표시).
export type SkuOrderRuleStatus = "PENDING" | "APPLIED" | "SKIPPED" | "NO_ORDER_RULE";

export interface SkuProduct {
  id: number;
  name: string;
  supplyPrice: number;
  costPrice: number;
  retailPrice: number;
  // 공급가 통화(상품 통화, 없으면 세트 통화). 예상 매입가 환율은 이 통화로 찾는다.
  currency: string;
  // 부대비용 계수 — 주문서(오더시트 시세 비교)와 같은 재료. 예상 매입가 = 공급가 × (1 + uplift) × 환율
  uplift: number;
  upliftFactors: { key: string; rate: number }[];
  priceAdjustedBy: {
    by: "MARKUP" | "DISCOUNT" | string;
    value: number;
  } | null;
  lineSheetSet: {
    id: number;
    name: string;
    boutiqueName: string;
    publishedAt: string | null;
    closedAt: string | null;
    orderRuleStatus?: SkuOrderRuleStatus | null;
  };
  [key: string]: any;
}

// 포이즌(득물) 시세. 판매량은 최근 30일 롤링 관측값이라 키도 sales30d로 고정이다.
export interface SkuPoizonInfo {
  spuId: number;
  showSpuId: number | null;
  minPrice: number | null;
  dealLowestPrice: number | null;
  sales30d: number | null;
  priceHistory: SkuDailyMarketPrice[];
  updatedAt: string | null;
}

// 크림 기준가 — 주문서 비교 기준(판매가능 최저가 lowestValue). 체결 2건 미만이면 마진 계산 불가.
export interface SkuKreamMarket {
  lowestValue: number | null;
  lastValue: number | null;
  salesCount: number | null;
  marginAvailable: boolean;
}

export interface SkuInfo {
  sku: string;
  images: string[];
  products: SkuProduct[];
  saleHistory: SkuDailyMarketPrice[];
  marketPriceAvg: number | null;
  kreamProductId: number | null;
  saleHistoryUpdatedAt: string | null;
  poizon: SkuPoizonInfo | null;
  kreamMarket: SkuKreamMarket | null;
  // 통화별 원화 환율(그날의 기준가) — 주문서와 같은 값. 못 받은 통화는 빠진다.
  pricing: { rates: Record<string, number> } | null;
}

export interface GetSkuPathParameter {
  sku: string;
  // 우노 수수료는 바이어별이라 보는 바이어를 같이 보낸다
  buyerId?: number;
}

export interface GetSkuRequest {}

export type GetSkuResponse = SkuInfo;

export const getSkuKey: YWRKey<GetSkuPathParameter> = {
  url: ({ sku, buyerId }) =>
    `/sku/${encodeURIComponent(sku)}${buyerId ? `?buyer_id=${buyerId}` : ""}`,
  method: "get",
};

export default function useGetSku() {
  const fetcher = useFetcher<GetSkuRequest, GetSkuResponse>();
  return useYWR(getSkuKey, fetcher);
}
